import React, { useState, useRef, useCallback, useEffect } from "react";
import { CreditCard, MapPin, Search, RefreshCw, AlertCircle, Plus, ChevronLeft, Car, Database } from "lucide-react";
import TransactionDetailsPanel from "./TransactionDetailsPanel";
import FastagMap from "./FastagMap";
import TransactionSidebar from "./TransactionSidebar";
import {
  useTrackedVehicles,
  useFastagDetails,
  useFastagFromDB,
  useAddTrackedVehicle,
} from "../../hooks/useFastagQueries";

const DIR_LABEL = { N: "North", S: "South", E: "East", W: "West" };

const Fastag = () => {
  const [searchValue, setSearchValue]                 = useState("");
  const [selectedVehicleNo, setSelectedVehicleNo]     = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showAddBanner, setShowAddBanner]             = useState(false);
  const mapRef = useRef(null);

  const { data: vehicles = [], isLoading: vehiclesLoading } = useTrackedVehicles();

  const {
    data: ulipTransactions = [],
    isLoading: ulipLoading,
    isError: ulipIsError,
    error: ulipErrorObj,
    refetch: refetchUlip,
    isSuccess: ulipSuccess,
  } = useFastagDetails(selectedVehicleNo);

  const {
    data: dbTransactions = [],
    isLoading: dbLoading,
    isError: dbIsError,
  } = useFastagFromDB(selectedVehicleNo, {
    enabled: !!selectedVehicleNo && ulipIsError,
  });

  const isUsingCache  = ulipIsError && !dbIsError && dbTransactions.length > 0;
  const transactions  = ulipIsError ? dbTransactions : ulipTransactions;
  const txnLoading    = ulipLoading || (ulipIsError && dbLoading);
  const totalFailure  = ulipIsError && dbIsError;
  const noData        = ulipIsError && !dbIsError && dbTransactions.length === 0 && !dbLoading;

  useEffect(() => {
    if ((ulipSuccess || isUsingCache) && transactions.length > 0) {
      setTimeout(() => mapRef.current?.focusOnRoute(transactions), 300);
    }
  }, [ulipSuccess, isUsingCache, transactions]);

  const addVehicleMutation = useAddTrackedVehicle();

  const selectedVehicle = selectedVehicleNo
    ? (vehicles.find((v) => v.vehicleRegNo === selectedVehicleNo) ?? { vehicleRegNo: selectedVehicleNo })
    : null;

  const uniquePlazas  = new Set(transactions.map((t) => t.tollPlazaName)).size;
  const directions    = transactions.reduce((acc, t) => { acc[t.laneDirection] = (acc[t.laneDirection] || 0) + 1; return acc; }, {});
  const mostCommonDir = Object.entries(directions).sort((a, b) => b[1] - a[1])[0]?.[0];

  const ulipStatus = ulipErrorObj?.response?.status;
  const errorMsg   = totalFailure
    ? ulipStatus === 404 ? `No FASTag records found for ${selectedVehicleNo}` : "FASTag lookup unavailable and no cached data found."
    : null;
  const errorType  = totalFailure ? (ulipStatus === 404 ? "not_found" : "failed") : null;

  const handleVehicleClick = (vehicle) => {
    setSelectedVehicleNo(vehicle.vehicleRegNo);
    setSelectedTransaction(null);
    setShowAddBanner(false);
    setSearchValue("");
  };

  const handleBackToList = () => {
    setSelectedVehicleNo(null);
    setSelectedTransaction(null);
    setShowAddBanner(false);
    setSearchValue("");
  };

  const handleSearch = () => {
    const vn = searchValue.replace(/[\s\-]/g, "").toUpperCase();
    if (vn.length < 6) return;
    const exists = vehicles.find((v) => v.vehicleRegNo === vn);
    if (exists) handleVehicleClick(exists);
    else setShowAddBanner(true);
  };

const handleAddVehicle = () => {
  const vn = searchValue.replace(/[\s\-]/g, "").toUpperCase();
  addVehicleMutation.mutate(
    { vehicleRegNo: vn, vehicleType: null },
    {
      onSuccess: () => {
        // Cache is already seeded by useAddTrackedVehicle's onSuccess
        // Just update UI state
        setShowAddBanner(false);
        setSelectedVehicleNo(vn);
        setSelectedTransaction(null);
        setSearchValue("");
      },
      onError: (err) => {
        const msg = err?.response?.data?.error || err.message || "Failed to add vehicle";
        // show a toast/alert here
        console.error("Add vehicle failed:", msg);
      },
    }
  );
};

  const handleTransactionSelect = useCallback((txn) => {
    setSelectedTransaction(txn);
    mapRef.current?.focusOnTransaction(txn);
  }, []);

  const handleCloseDetails = useCallback(() => setSelectedTransaction(null), []);
  const handleRefresh      = () => selectedVehicleNo && refetchUlip();
  const showingMap         = !!selectedVehicle;

  return (
    <div className="flex flex-col h-screen bg-slate-100 overflow-hidden font-sans">

      {/* ── Header ── */}
      <div className="bg-slate-900 text-white px-5 py-3 flex items-center justify-between gap-4 flex-shrink-0 shadow-lg">
        <div className="flex items-center gap-3">
          {showingMap && (
            <button
              onClick={handleBackToList}
              className="flex items-center gap-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-600 px-2.5 py-1.5 rounded-lg border border-slate-600 transition-colors"
            >
              <ChevronLeft size={13} />
              Vehicles
            </button>
          )}
          {/* Logo */}
          <div className="w-8 h-8 rounded-lg bg-amber-400 flex items-center justify-center text-slate-900 font-black text-base shrink-0">
            ⬡
          </div>
          <div>
            <h1 className="text-sm font-semibold leading-tight tracking-tight">FASTag Tracker</h1>
            <p className="text-[10px] text-slate-400 leading-none mt-0.5">Live toll & location intelligence</p>
          </div>
          {selectedVehicleNo && (
            <span className="font-mono text-xs font-bold bg-slate-700 px-3 py-1 rounded-full tracking-widest border border-slate-600">
              {selectedVehicleNo}
            </span>
          )}
        </div>

        {/* Stat cards — only when data exists */}
        {transactions.length > 0 && (
          <div className="flex items-center gap-2">
            <StatCard label="Transactions" value={transactions.length} />
            <StatCard label="Plazas"       value={uniquePlazas} color="bg-emerald-600" />
            <StatCard label="Vehicle Type" value={transactions[0]?.vehicleType || "—"} />
            {mostCommonDir && <StatCard label="Direction" value={DIR_LABEL[mostCommonDir] || mostCommonDir} />}
          </div>
        )}

        <button
          onClick={handleRefresh}
          disabled={!selectedVehicleNo || txnLoading}
          className="flex items-center gap-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg border border-slate-600 transition-colors"
        >
          <RefreshCw size={13} className={txnLoading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* ── Search bar ── */}
      <div className="bg-white border-b border-slate-200 px-5 py-2.5 flex items-center gap-3 flex-shrink-0">
        <Search size={14} className="text-slate-400 flex-shrink-0" />
        <input
          type="text"
          placeholder="Search or enter vehicle number…"
          value={searchValue}
          onChange={(e) => { setSearchValue(e.target.value.replace(/[\s\-]/g, "").toUpperCase()); setShowAddBanner(false); }}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono tracking-widest text-slate-800 placeholder:font-sans placeholder:tracking-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
        />
        <button
          onClick={handleSearch}
          disabled={txnLoading || !searchValue.trim()}
          className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 text-xs font-bold px-4 py-2 rounded-lg transition-colors flex-shrink-0"
        >
          <Search size={13} />
          {txnLoading ? "Searching…" : "Track Vehicle"}
        </button>
      </div>

      {/* ── Cache banner ── */}
      {isUsingCache && (
        <div className="bg-amber-50 border-b border-amber-200 px-5 py-2 flex items-center justify-between gap-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Database size={13} className="text-amber-500 flex-shrink-0" />
            <p className="text-xs text-amber-700">ULIP unavailable — showing cached data from database.</p>
          </div>
          <button onClick={handleRefresh} className="flex items-center gap-1.5 text-xs font-medium text-amber-600 hover:text-amber-800 border border-amber-300 hover:border-amber-400 px-2.5 py-1 rounded-lg transition-colors flex-shrink-0">
            <RefreshCw size={11} /> Retry ULIP
          </button>
        </div>
      )}

      {/* ── Add vehicle banner ── */}
      {showAddBanner && (
        <div className="bg-amber-50 border-b border-amber-200 px-5 py-2.5 flex items-center justify-between gap-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <AlertCircle size={14} className="text-amber-500 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              <span className="font-mono font-bold">{searchValue}</span> is not in your tracked list.
            </p>
          </div>
          <button
            onClick={handleAddVehicle}
            disabled={addVehicleMutation.isPending}
            className="flex items-center gap-1.5 bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-slate-900 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
          >
            <Plus size={12} />
            {addVehicleMutation.isPending ? "Adding…" : "Add & Track"}
          </button>
        </div>
      )}

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* VIEW 1: Vehicle list */}
        {!showingMap && (
          <VehicleListView
            vehicles={vehicles}
            isLoading={vehiclesLoading}
            loadingVehicleNo={txnLoading ? selectedVehicleNo : null}
            onVehicleClick={handleVehicleClick}
          />
        )}

        {/* VIEW 2: Map layout */}
        {showingMap && (
          <>
            {txnLoading && (
              <div className="flex flex-col items-center justify-center flex-1 gap-3 text-slate-400">
                <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm">{ulipLoading ? "Fetching FASTag records from ULIP…" : "Loading cached records…"}</p>
              </div>
            )}

            {!txnLoading && totalFailure && (
              <div className="flex flex-col items-center justify-center flex-1 gap-3">
                <AlertCircle size={36} className="text-rose-300" strokeWidth={1} />
                <p className="text-sm text-slate-500">{errorMsg}</p>
                {errorType === "failed" && (
                  <button onClick={handleRefresh} className="text-xs text-amber-600 border border-amber-300 px-3 py-1.5 rounded-lg hover:bg-amber-50 transition">Retry</button>
                )}
              </div>
            )}

            {!txnLoading && noData && (
              <div className="flex flex-col items-center justify-center flex-1 gap-3 text-slate-300">
                <Database size={36} strokeWidth={1} />
                <p className="text-sm text-slate-400">No cached records found for this vehicle.</p>
                <button onClick={handleRefresh} className="text-xs text-amber-600 border border-amber-300 px-3 py-1.5 rounded-lg hover:bg-amber-50 transition">Retry ULIP</button>
              </div>
            )}

            {!txnLoading && !totalFailure && !noData && transactions.length === 0 && (
              <div className="flex flex-col items-center justify-center flex-1 gap-3 text-slate-300">
                <MapPin size={40} strokeWidth={1} />
                <p className="text-sm text-slate-400">No transactions found</p>
              </div>
            )}

            {!txnLoading && !totalFailure && transactions.length > 0 && (
              <>
                <TransactionSidebar
                  transactions={transactions}
                  selectedTransaction={selectedTransaction}
                  onTransactionSelect={handleTransactionSelect}
                />
                <div className="flex-1 relative overflow-hidden">
                  <FastagMap
                    ref={mapRef}
                    transactions={transactions}
                    selectedTransaction={selectedTransaction}
                    onTransactionSelect={handleTransactionSelect}
                  />
                </div>
                {selectedTransaction && (
                  <TransactionDetailsPanel
                    transaction={selectedTransaction}
                    onClose={handleCloseDetails}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ── Sub-components ── */

const StatCard = ({ label, value, color = "bg-slate-700" }) => (
  <div className={`${color} rounded-lg px-3 py-1.5 text-center min-w-[72px] border border-white/10`}>
    <p className="text-[10px] text-white/60 leading-none">{label}</p>
    <p className="text-sm font-semibold text-white leading-none mt-1">{value}</p>
  </div>
);

const GRID_THRESHOLD = 12;

const VehicleListView = ({ vehicles, isLoading, loadingVehicleNo, onVehicleClick }) => {
  const [listSearch, setListSearch] = useState("");
  const filtered = vehicles.filter((v) => v.vehicleRegNo.toLowerCase().includes(listSearch.toLowerCase()));
  const useList  = vehicles.length > GRID_THRESHOLD;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-3 text-slate-400">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm">Loading vehicles…</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-6 pt-6 pb-3 flex-shrink-0">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Tracked Vehicles
          <span className="ml-2 bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-bold">{vehicles.length}</span>
        </p>
        {useList && (
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 w-56">
            <Search size={13} className="text-slate-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Filter vehicles…"
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value.toUpperCase())}
              className="flex-1 text-xs font-mono text-slate-700 bg-transparent focus:outline-none placeholder:font-sans placeholder:text-slate-300"
            />
            {listSearch && <button onClick={() => setListSearch("")} className="text-slate-300 hover:text-slate-500">✕</button>}
          </div>
        )}
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center flex-1 gap-2 text-slate-300">
          <Car size={32} strokeWidth={1} />
          <p className="text-sm text-slate-400">{listSearch ? `No vehicles match "${listSearch}"` : "No tracked vehicles yet"}</p>
        </div>
      )}

      {/* Grid view (≤12 vehicles) */}
      {!useList && filtered.length > 0 && (
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filtered.map((vehicle) => (
              <VehicleCard
                key={vehicle.vehicleRegNo}
                vehicle={vehicle}
                isLoading={loadingVehicleNo === vehicle.vehicleRegNo}
                onClick={() => onVehicleClick(vehicle)}
              />
            ))}
          </div>
        </div>
      )}

      {/* List view (>12 vehicles) */}
      {useList && filtered.length > 0 && (
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 border-b border-slate-200">
              <span className="col-span-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">#</span>
              <span className="col-span-5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Vehicle No.</span>
              <span className="col-span-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Type</span>
              <span className="col-span-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-right">Action</span>
            </div>
            {filtered.map((vehicle, idx) => {
              const isLoading = loadingVehicleNo === vehicle.vehicleRegNo;
              return (
                <div
                  key={vehicle.vehicleRegNo}
                  onClick={() => onVehicleClick(vehicle)}
                  className="grid grid-cols-12 items-center px-4 py-3 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-amber-50 transition-colors group"
                >
                  <span className="col-span-1 text-xs text-slate-300 font-medium">{idx + 1}</span>
                  <span className="col-span-5 font-mono text-sm font-bold text-slate-800 tracking-wider group-hover:text-amber-700 transition-colors">{vehicle.vehicleRegNo}</span>
                  <span className="col-span-3">
                    <span className="text-[10px] font-medium bg-slate-100 group-hover:bg-amber-100 text-slate-500 group-hover:text-amber-700 px-2 py-0.5 rounded-full transition-colors">
                      {vehicle.vehicleType !== "—" ? vehicle.vehicleType : "Unknown"}
                    </span>
                  </span>
                  <div className="col-span-3 flex justify-end">
                    {isLoading
                      ? <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                      : <span className="text-[10px] text-amber-500 group-hover:text-amber-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity">Track →</span>
                    }
                  </div>
                </div>
              );
            })}
          </div>
          {listSearch && <p className="text-xs text-slate-400 mt-3 text-center">Showing {filtered.length} of {vehicles.length} vehicles</p>}
        </div>
      )}
    </div>
  );
};

const VehicleCard = ({ vehicle, isLoading, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white border border-slate-200 rounded-xl px-4 py-4 cursor-pointer hover:border-amber-400 hover:shadow-md hover:-translate-y-0.5 transition-all group"
  >
    <div className="flex items-start justify-between mb-3">
      <div className="w-8 h-8 rounded-lg bg-amber-400 flex items-center justify-center text-slate-900 font-black text-base shrink-0 group-hover:bg-amber-500 transition-colors">
        ⬡
      </div>
      {isLoading
        ? <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
        : <span className="text-[10px] font-medium bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
            {vehicle.vehicleType !== "—" ? vehicle.vehicleType : "Unknown"}
          </span>
      }
    </div>
    <p className="font-mono text-sm font-bold text-slate-800 tracking-wider">{vehicle.vehicleRegNo}</p>
    <p className="text-[11px] text-slate-400 mt-1">Click to track</p>
  </div>
);

export default Fastag;